/**
 * ログインフォームコンポーネント
 * レスポンシブ・ダークモード対応
 */

import React, { useRef, useEffect } from 'react';
import { useForm } from '@mantine/form';
import {
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Alert,
  Paper,
  Title,
  Text,
  Center,
  Box,
  Image,
  Divider,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconMail,
  IconLock,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useLogin } from '@/features/auth/hooks/useAuth';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { loginFormSchema, type LoginFormValues } from '@/features/auth/schema';
import { useFocusManagement } from '../../../shared/hooks/useFocusManagement';
import { FocusDebugger } from '../../../shared/components/accessibility/FocusDebugger';
// import { LoginFormDebugger } from './LoginFormDebugger';

export const LoginForm: React.FC = () => {
  const loginMutation = useLogin();
  const authContext = useAuth();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  // フォーカス制御のためのrefs
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  // フォーカス管理フック
  const { containerRef, focusError, focusElement } = useFocusManagement({
    autoFocus: true,
    restoreFocus: true,
    initialDelay: 200,
  });

  // 初期フォーカス設定
  useEffect(() => {
    // ページ読み込み時にメールアドレス入力欄に自動フォーカス
    const timer = setTimeout(() => {
      if (emailRef.current) {
        emailRef.current.focus();
      }
    }, 100); // DOM更新後にフォーカス

    return () => clearTimeout(timer);
  }, []);

  // フォーム管理（Mantine Form + Zodバリデーション）
  const form = useForm<LoginFormValues>({
    initialValues: {
      email: '',
      password: '',
      remember: false,
    },
    validate: (values) => {
      try {
        // zodResolverの問題を回避するため、手動でZodバリデーション
        const result = loginFormSchema.safeParse(values);
        if (!result.success) {
          const errors: Record<string, string> = {};
          // result.error.issuesを使用（Zod v3の正しいプロパティ）
          if (result.error && result.error.issues) {
            result.error.issues.forEach((issue) => {
              const path = issue.path[0];
              if (path) {
                errors[String(path)] = issue.message;
              }
            });
          }
          return errors;
        }
        return {};
      } catch (e) {
        // エラーが発生した場合は空のオブジェクトを返す
        console.error('Validation error:', e);
        return {};
      }
    },
    validateInputOnChange: true, // リアルタイムバリデーション有効化
  });

  // キーボードナビゲーション処理
  const handleKeyPress = (e: React.KeyboardEvent, currentField: 'email' | 'password') => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      if (currentField === 'email') {
        // メールアドレス入力欄でEnter → パスワード入力欄へ移動
        if (passwordRef.current) {
          passwordRef.current.focus();
        }
      } else if (currentField === 'password') {
        // パスワード入力欄でEnter → フォーム送信（バリデーション成功時）
        if (form.isValid()) {
          handleSubmit(e as any);
        } else {
          // バリデーションエラーがある場合は、最初のエラーフィールドにフォーカス
          const errors = form.validate();
          if (errors.email && emailRef.current) {
            emailRef.current.focus();
          } else if (errors.password && passwordRef.current) {
            passwordRef.current.focus();
          }
        }
      }
    }
  };

  // フォーム送信処理（TanStack Query v5 + Zod）
  const handleSubmit = form.onSubmit(async (values) => {
    // ログイン実行（TanStack Query v5）
    loginMutation.mutate(values);
  });

  return (
    <>
      {/* デバッガー（開発環境のみ） */}
      <FocusDebugger />
      {/* <LoginFormDebugger form={form} isPending={loginMutation.isPending} /> */}
      
      <Box 
        ref={containerRef}
        role="main"
        aria-label="ログインフォーム"
        style={{
          position: 'relative',
          marginTop: '60px',
          width: '100%',
          maxWidth: '400px',
          margin: '0 auto',
        }}
      >
      {/* ロゴ（カードから半分はみ出す） */}
      <Box
        style={{
          position: 'absolute',
          top: '-60px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          width: '120px',
          height: '120px',
          filter: isDark 
            ? 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3))' 
            : 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
        }}
      >
        <Image
          src="/public/images/s-logo.png"
          alt="SuienLinks Logo"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      <Paper
        radius="md"
        shadow="md"
        p={{ base: 'md', sm: 'xl' }}
        pt={{ base: 60, sm: 80 }}
        w="100%"
        style={{
          backgroundColor: isDark 
            ? 'rgba(26, 27, 30, 0.95)' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark 
            ? 'rgba(255, 255, 255, 0.1)' 
            : 'rgba(0, 0, 0, 0.1)'}`,
        }}
      >
        {/* タイトル */}
        <Center mb="xl">
          <Stack gap="xs" align="center">
            <Title
              order={2}
              c="blue"
            >
              SUIEN LINKS
            </Title>
            <Text size="sm" c="dimmed">
              -水縁-
            </Text>
          </Stack>
        </Center>

      <form onSubmit={handleSubmit}>
        <Stack>
          {/* エラー表示（TanStack Query v5） */}
          {loginMutation.isError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              title="ログインエラー"
            >
              {loginMutation.error?.response?.data?.message || 
               loginMutation.error?.message || 
               'ログインに失敗しました'}
            </Alert>
          )}

          {/* メールアドレス入力 */}
          <TextInput
            ref={emailRef}
            required
            label="メールアドレス"
            placeholder="example@email.com"
            type="email"
            leftSection={<IconMail size={18} />}
            autoComplete="email"
            onKeyDown={(e) => handleKeyPress(e, 'email')}
            data-autofocus
            {...form.getInputProps('email')}
          />

          {/* パスワード入力 */}
          <PasswordInput
            ref={passwordRef}
            required
            label="パスワード"
            placeholder="パスワードを入力"
            leftSection={<IconLock size={18} />}
            autoComplete="current-password"
            onKeyDown={(e) => handleKeyPress(e, 'password')}
            {...form.getInputProps('password')}
          />

          {/* ログインボタン（グラデーション & ローディング状態） */}
          <Button
            ref={submitButtonRef}
            fullWidth
            type="submit"
            loading={loginMutation.isPending}
            size="md"
            variant="gradient"
            gradient={{ from: 'blue', to: 'cyan', deg: 45 }}
            mt="md"
            loaderProps={{ size: 'sm' }}
            tabIndex={0}
            styles={{
              root: {
                transition: 'all 0.3s ease',
                '&:hover:not([data-loading])': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                },
                '&:focus': {
                  outline: '2px solid rgba(59, 130, 246, 0.6)',
                  outlineOffset: '2px',
                },
              },
            }}
          >
            {loginMutation.isPending ? 'ログイン中...' : 'ログイン'}
          </Button>
        </Stack>
      </form>

        {/* フッター：制作会社情報 */}
        <Divider my="lg" />
        <Center>
          <Text size="xs" c="dimmed">
            Developed by D-Studio
          </Text>
        </Center>

        {/* 隠しヘルプテキスト（アクセシビリティ対応） */}
        <div style={{ position: 'absolute', left: '-9999px' }}>
          <div id="email-help">メールアドレスを入力してください。Enterキーで次のフィールドに移動します。</div>
          <div id="password-help">パスワードを入力してください。Enterキーでログインを実行します。</div>
        </div>
      </Paper>
    </Box>
    </>
  );
};